import React, {useEffect, useRef, useState} from "react";
import styled from "styled-components";
import axios from "axios";
import FairInfoBar from "../FairInfoBar";
import { useNavigate } from "react-router-dom";
import { WhiteButton } from "../Button";
import Heading from "../Heading";
import { handleGenericErrors, handleGenericSuccess } from "../../utils/ErrorUtility";
import TextLink from "../TextLink";
import Email from "../../images/email.png";
import Comment from "../../images/comment.png";
import { convertToCAD, convertToUSD } from "../../utils/CurrencyConversion";
import { useReactToPrint } from "react-to-print";
import RegisterInvoice from "./Invoice/RegisterInvoice";
import { trackFairDetailsGiveFeedback, trackFinFormReviseSubmission, trackFinFormPrintInvoice, trackFinFormSpendScholasticDollars, trackFinFormPageChange, updateUserFairId } from "../../DumbleData";

interface Props {
	language: string;
}

export const FinFormConfirmationPage = ( {language} : Props ) => {
    const [fairInfo, setFairInfo] = useState<any>();
    const [fairId, setFairId] = useState<string>("");
    const [latestStep, setLatestStep] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [totalDue, setTotalDue] = useState<string>("0.00");
    const componentRef = useRef<HTMLDivElement>(null);
	const [printClicked, setPrintClicked] = useState(false);
    const navigate = useNavigate();
    
    const handleBack = async () => {
        trackFinFormReviseSubmission();
        navigate("/financial-form/review");
    }

    const handlePrint = () => {
        openPrintDialogue();
        setPrintClicked(false);
	};

	const openPrintDialogue = useReactToPrint({
		content: () => componentRef.current as HTMLElement,
        documentTitle: `Invoice_${fairId}`,
        onBeforePrint: () => { document.title = `Invoice_${fairId}`; },
        onAfterPrint: () => { document.title = "CA Book Fair Homepage"; },
	});

	const handleClickPrint = () => {
        trackFinFormPrintInvoice();
		setPrintClicked(true);
	};

    // GET Request
    useEffect(() => {
        const getFinancialFormConfirmation = async () => {
            try {
                await axios.get(
                    `${process.env.REACT_APP_ENV_BASE_URL}/api/user/fairs/current/financials/form/invoice`,
                        {
                            withCredentials: true,
                        }
                )
                .then((response) => {
                    if (response.status === 200) {
                        if (response.data.finFormState !== "SUBMITTED" && response.data.finFormState !== "CLOSED") {
                            navigate("/financial-form/intro");
                        }

                        const fairInfo = {
                            name: response.data.fairName,
                            start: response.data.startDate,
                            end: response.data.endDate,
                            enableSwitch: response.data.enableSwitch
                        }
                        updateUserFairId(response.data.fairId);

                        setFairInfo(fairInfo);
                        setFairId(response.data.fairId);
                        setLatestStep(response.data.finFormState);
                        setTotalDue(response.data.amountDue.amountDue.toFixed(2));
                    }
                    else {
                        handleGenericSuccess(response);
                    }
                })
                .catch((err) => {
                    handleGenericErrors(err);
                });
            } catch (err: any) {
                handleGenericErrors(err);
            } finally {
                setIsLoading(false);
                // scroll to top of confirmation page when it loads
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
                trackFinFormPageChange("Financial Form Confirmation Page");
            }
        };
        getFinancialFormConfirmation();
    }, []);

    return (<>
        {!isLoading && (<>
            <>
                <InvoicePDF>
                    <div ref={componentRef}>
                        {printClicked && (
                            <RegisterInvoice
                                language={language}
                                handlePrint={handlePrint}
                                printClicked={printClicked}
                            />
                        )}
                    </div>
                </InvoicePDF>
            </>
            <FairInfoBar
                language={language}
                enableSwitch={fairInfo.enableSwitch}
                fairInfoData={fairInfo}
                organizationData={fairInfo}
                pageType={"financial-form"}
                />

            <StyledDiv>
                <StyledHeading>{language === "en" ? "Thank you for submitting your Financial Form" : "Merci d’avoir soumis votre formulaire financier"}</StyledHeading>
                <StyledBody>
                    {language === "en" ? 
                    "Scholastic Dollars will be issued once Fair payment has been received in full and processed." : 
                    "Les dollars Scholastic seront émis une fois que le paiement complet pour votre Festival aura été reçu et traité."}
                </StyledBody>
                <StyledBody className="bold">
                    {language === "en" ? 
                    "Please issue one cheque for this amount payable to Scholastic Book Fairs Canada and remit within 7 days following your Fair" : 
                    "Veuillez émettre un chèque pour ce montant à l’ordre de Scholastic Book Fairs Canada et le remettre dans les 7 jours suivant votre Festival."}
                </StyledBody>
                <StyledBody>
                    {language === "en" ? 
                    "Don't forget to send in a copy of the Financial Form invoice, all credit card and Interac/debit receipts, manual imprinter transactions (if applicable), and End of Day Reports along with payment." : 
                    "N’oubliez pas d’envoyer une copie de la facture du formulaire financier, tous les reçus des transactions par carte de crédit et d’Interac/débit, les empreintes des transactions manuelles (le cas échéant) et les rapports de fin de journée avec le paiement."}
                </StyledBody>
                {latestStep !== "CLOSED" ? (
                    <>
                        <StyledBody className="totalDue">{language === "en" ? `Total Amount Due: $${convertToUSD(totalDue)}` : `Montant total dû : ${convertToCAD(totalDue)} $`}</StyledBody>
                        <StyledWhiteButton handleClick={handleBack}>{language === "en" ? "Revise Submission" : "Réviser la soumission"}</StyledWhiteButton>
                    </>
                    ):(<StyledBreak/>)
                }
                <Cards>
                    {/* Invoice */}
                    <StyledCard>
                        <StyledImg src={Email} alt="" />
                        <StyledTextLink handleClick={handleClickPrint} target="_blank">
                            {language === "en" ? "Print invoice and mail with payment" : "Imprimer la facture et l’envoyer avec le paiement"}
                        </StyledTextLink>
                    </StyledCard>

                    {/* Feedback */}
                    <StyledCard>
                        <StyledImg src={Comment} alt="" />
                        <StyledTextLink handleClick={trackFairDetailsGiveFeedback} href={language === "en" ? "https://www.surveymonkey.com/r/BF24-25" : "https://fr.surveymonkey.com/r/FDLFrench24-25"} target="_blank">
                            {language === "en" ? "Give feedback" : "Envoyer des commentaires"}
                        </StyledTextLink>
                    </StyledCard>
                </Cards>

                <ScholasticDollars>
                    <StyledTextLink handleClick={trackFinFormSpendScholasticDollars} href={language === "en" ? "https://www2.scholastic.ca/bookfairs/learning-lab-scholastic-dollars/" : "https://www2.scholastic.ca/bookfairs/info-festival-dollars-scholastic/"} target="_blank">
                        {language === "en" ? "Spend your Scholastic Dollars" : "Utiliser vos dollars Scholastic"}
                    </StyledTextLink>
                </ScholasticDollars>
            </StyledDiv>
        </>)}
    </>);
};

export default FinFormConfirmationPage;

const InvoicePDF = styled.div`
    display: none;
`;

const StyledImg = styled.img`
    margin-bottom: 14px;
    float: right;
    @media (max-width: 959px) {
        float: left;
    }
`;

const StyledTextLink = styled(TextLink)`
    width: 159px;
	text-align: center;
	-webkit-font-smoothing: antialiased;
    font-family: museo-sans;
    overflow-wrap: anywhere;
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    color: #E81111;
	margin: 0;
	padding: 0;
    font-style: normal;
`;

const ScholasticDollars = styled.div`
    margin: 57px auto;
`;

const StyledDiv = styled.div`
    text-align: center;
`;

const StyledHeading = styled(Heading)`
    max-width: 600px;
    color: #333;
    text-align: center;
    font-family: museo-sans;
    font-size: 39px;
    font-style: normal;
    font-weight: 300;
    line-height: 42px; /* 107.692% */
    margin: 83px auto 26px auto;
`;

const StyledBody = styled.p`
    max-width: 397px;
    color: #333;
    text-align: center;
    font-family: museo-sans;
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 15px;
    margin: 0px auto;
    @media (min-width: 561px) {
        white-space: pre-wrap;
    }
    &.totalDue {
        font-size: 16px;
        line-height: 19px;
        margin: 44px auto;
    }
    &.bold {
        font-weight: 700;
        margin-top: 10px;
        margin-bottom: 10px;
    }
`;

const Cards = styled.div`
    display: flex;
    gap: 40px;
    justify-content: center;
    @media (max-width: 540px) {
        padding: 0 40px;
        flex-direction: column;
    }
`;

const StyledCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #F5F5F5;
    max-width: 600px;
    min-width: 159px;
    padding: 48px;
    border-radius: 16px;
    @media (max-width: 720px) {
        padding: 36px;
    }
    @media (max-width: 519px) {
        padding: 24px;
    }
`;

const StyledWhiteButton = styled(WhiteButton)`
    margin: 0px auto 60px auto;
    color: #E81111;
    text-align: center;
    font-size: 11px;
    font-style: normal;
    text-transform: uppercase;
    height: 40px;
    font-family: museo-sans;
    font-weight: 700;
    line-height: 12px;
    letter-spacing: 1.5px;
    display: flex;
    padding: 9px 44px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 40px;
    border: 1px solid #E81111;
    background: #FFF;
    padding: 9px 30px;
`;

const StyledBreak = styled.div`
    height: 65px;
`;
